// Settings (plan step 2.2): one page a section, the sidebar its navigation. The project's pages
// (General, Apps and keys, Symbols, Issue grouping, APM link; APS-319; Data export, APS-200), the
// organization's and the account's (MembersSettings, Activity, UserErasureSettings,
// AccountSettings; APS-320) are their own files. Admin actions answer 403 for members; the pages say so instead of hiding them.
import { useParams } from '@tanstack/react-router'
import { SandboxReadOnly } from '../components/SandboxReadOnly'
import { useI18n } from '../i18n'
import { sandboxFor } from '../sandbox'
import { PreferencesPage, ProfilePage } from './AccountSettings'
import { ActivityPage } from './Activity'
import { AgentsSettings } from './AgentsSettings'
import { DestinationsPage } from './AlertsSettings'
import { ApmSettings } from './ApmSettings'
import { AppsSettings } from './AppsSettings'
import { DataExportSettings } from './DataExportSettings'
import { GeneralSettings } from './GeneralSettings'
import { GroupingSettings } from './GroupingSettings'
import { IntegrationsPage } from './IntegrationsSettings'
import { MembersPage } from './MembersSettings'
import { PlanTab } from './PlanSettings'
import { useProjectParams } from './params'
import { SymbolsSettings } from './SymbolsSettings'
import { isSettingsSection, type SettingsSection } from './search'
import { PrivacyPage } from './UserErasureSettings'

/** The page, or in the sandbox, which cannot change anything, the reason (D64). */
export function SettingsPage() {
  const { projectId } = useProjectParams()
  return sandboxFor(projectId) ? <SandboxReadOnly /> : <SettingsView />
}

function SettingsView() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const { section: raw } = useParams({ strict: false }) as { section?: string }
  const section: SettingsSection = isSettingsSection(raw) ? raw : 'general'
  const labels: Record<SettingsSection, string> = {
    general: t('settings.tab.general'),
    apps: t('settings.tab.apps'),
    symbols: t('settings.tab.symbols'),
    grouping: t('settings.tab.grouping'),
    apm: t('settings.tab.apm'),
    agents: t('settings.tab.agents'),
    data: t('settings.tab.data'),
    alerts: t('settings.tab.alerts'),
    privacy: t('settings.tab.privacy'),
    members: t('settings.tab.members'),
    integrations: t('settings.tab.integrations'),
    plan: t('settings.tab.plan'),
    activity: t('settings.tab.activity'),
    profile: t('settings.tab.profile'),
    preferences: t('settings.tab.preferences'),
  }
  const group =
    section === 'general' ||
    section === 'apps' ||
    section === 'symbols' ||
    section === 'grouping' ||
    section === 'apm' ||
    section === 'agents' ||
    section === 'data'
      ? t('settings.group.project')
      : section === 'profile' || section === 'preferences'
        ? t('settings.group.account')
        : t('settings.group.organization')
  const crumb = `${t('settings.title')} / ${group}`
  const head = { crumb, title: labels[section] }
  const page = { orgId, projectId, ...head }
  // Sections on Console D (the mock's `.pg.se-pg`) draw their own page and head (meta, actions).
  const d =
    section === 'general' ? (
      <GeneralSettings {...page} />
    ) : section === 'apps' ? (
      <AppsSettings {...page} />
    ) : section === 'symbols' ? (
      <SymbolsSettings {...page} />
    ) : section === 'grouping' ? (
      <GroupingSettings {...page} />
    ) : section === 'apm' ? (
      <ApmSettings {...page} />
    ) : section === 'data' ? (
      <DataExportSettings {...page} />
    ) : section === 'members' ? (
      <MembersPage orgId={orgId} {...head} />
    ) : section === 'activity' ? (
      <ActivityPage orgId={orgId} {...head} />
    ) : section === 'privacy' ? (
      <PrivacyPage orgId={orgId} projectId={projectId} {...head} />
    ) : section === 'profile' ? (
      <ProfilePage orgId={orgId} {...head} />
    ) : section === 'preferences' ? (
      <PreferencesPage {...head} />
    ) : section === 'integrations' ? (
      <IntegrationsPage orgId={orgId} projectId={projectId} {...head} />
    ) : section === 'plan' ? (
      <PlanTab orgId={orgId} {...head} />
    ) : section === 'alerts' ? (
      <DestinationsPage orgId={orgId} projectId={projectId} {...head} />
    ) : section === 'agents' ? (
      <main id="main" className="pg se-pg agents-settings">
        <AgentsSettings orgId={orgId} projectId={projectId} {...head} />
      </main>
    ) : null
  // Each renders its own `main.pg.se-pg <section>`; the page class scopes its rules (settings.css).
  return d
}
